/**
 * Kundenakte: Dokumente, Kontakthistorie, Verträge.
 *
 * Alle Wege hängen unter dem Kunden. Das ist keine Kosmetik: die
 * Zugehörigkeit steht damit in der Adresse und kann auf dem Server nicht
 * vergessen werden.
 *
 * Nichts davon liegt im Offlinespeicher. Eine Akte pflegt man im Büro; auf
 * dem Platz zählt der Prüfbogen, und ein Vertrag, der offline geändert und
 * später hochgeschoben wird, wäre eine Quelle für Überraschungen.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './klient'

export type Dokumentart =
  | 'vertrag'
  | 'lageplan'
  | 'schriftverkehr'
  | 'gutachten'
  | 'rechnung'
  | 'sonstige'

export const DOKUMENTARTTEXT: Record<Dokumentart, string> = {
  vertrag: 'Vertrag',
  lageplan: 'Lageplan',
  schriftverkehr: 'Schriftverkehr',
  gutachten: 'Gutachten',
  rechnung: 'Rechnung',
  sonstige: 'Sonstiges',
}

export type Kontaktart = 'telefon' | 'email' | 'vorortermin' | 'brief' | 'notiz'

export const KONTAKTARTTEXT: Record<Kontaktart, string> = {
  telefon: 'Telefon',
  email: 'E-Mail',
  vorortermin: 'Vor Ort',
  brief: 'Brief',
  notiz: 'Notiz',
}

export type Ablageart = 'lokal' | 'sharepoint' | 'webdav' | 's3'

export const ABLAGEARTTEXT: Record<Ablageart, string> = {
  lokal: 'Auf dem Server',
  sharepoint: 'SharePoint (Microsoft 365)',
  webdav: 'Nextcloud oder WebDAV',
  s3: 'Storage-Server (S3)',
}

export type Dokument = {
  id: string
  kunde_id: string
  spielplatz_id: string | null
  name: string
  art: Dokumentart
  bemerkung: string | null
  inhaltstyp: string
  groesse: number
  sha256: string
  ablage_art: Ablageart
  im_portal_sichtbar: boolean
  hochgeladen_von: string | null
  erstellt_am: string
}

export type Kontakt = {
  id: string
  kunde_id: string
  zeitpunkt: string
  art: Kontaktart
  betreff: string
  inhalt: string | null
  gespraechspartner: string | null
  verfasser: string | null
  erstellt_am: string
}

export type Vertrag = {
  id: string
  kunde_id: string
  bezeichnung: string
  nummer: string | null
  pruefart: string | null
  turnus_monate: number | null
  preis: string | null
  je_platz: boolean
  beginn: string | null
  ende: string | null
  kuendigungsfrist_monate: number | null
  bemerkung: string | null
  aktiv: boolean
  laeuft: boolean
}

export const aktenschluessel = {
  dokumente: (kundeId: string) => ['kundenakte', kundeId, 'dokumente'] as const,
  kontakte: (kundeId: string) => ['kundenakte', kundeId, 'kontakte'] as const,
  vertraege: (kundeId: string) => ['kundenakte', kundeId, 'vertraege'] as const,
  ablage: ['ablage'] as const,
}

// ── Dokumente ──────────────────────────────────────────────────────────

export function useDokumente(kundeId: string) {
  return useQuery({
    queryKey: aktenschluessel.dokumente(kundeId),
    queryFn: ({ signal }) => api.holen<Dokument[]>(`/api/kunden/${kundeId}/dokumente`, signal),
    enabled: Boolean(kundeId),
  })
}

export function useDokumentHochladen(kundeId: string) {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (formular: FormData) =>
      api.hochladen<Dokument>(`/api/kunden/${kundeId}/dokumente`, formular),
    onSuccess: () => {
      void speicher.invalidateQueries({ queryKey: aktenschluessel.dokumente(kundeId) })
      // Die Verwaltung zeigt Anzahl und Belegung der Ablage.
      void speicher.invalidateQueries({ queryKey: aktenschluessel.ablage })
    },
  })
}

export function useDokumentAendern(kundeId: string) {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...felder }: { id: string } & Partial<Dokument>) =>
      api.aendern<Dokument>(`/api/kunden/${kundeId}/dokumente/${id}`, felder),
    onSuccess: () =>
      speicher.invalidateQueries({ queryKey: aktenschluessel.dokumente(kundeId) }),
  })
}

export function useDokumentLoeschen(kundeId: string) {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.loeschen(`/api/kunden/${kundeId}/dokumente/${id}`),
    onSuccess: () => {
      void speicher.invalidateQueries({ queryKey: aktenschluessel.dokumente(kundeId) })
      void speicher.invalidateQueries({ queryKey: aktenschluessel.ablage })
    },
  })
}

/**
 * Adresse zum Ansehen oder Herunterladen.
 *
 * Ein einfacher Link genügt: der Abruf ist ein GET, und das Sitzungs-Cookie
 * geht mit. Der Inhalt wird nicht über den Zwischenspeicher geholt – ein
 * Vertrag von 20 MB hat im Arbeitsspeicher der Anwendung nichts verloren.
 */
export function dokumentAdresse(kundeId: string, dokumentId: string): string {
  return `/api/kunden/${kundeId}/dokumente/${dokumentId}/inhalt`
}

// ── Kontakte ───────────────────────────────────────────────────────────

export function useKontakte(kundeId: string) {
  return useQuery({
    queryKey: aktenschluessel.kontakte(kundeId),
    queryFn: ({ signal }) => api.holen<Kontakt[]>(`/api/kunden/${kundeId}/kontakte`, signal),
    enabled: Boolean(kundeId),
  })
}

export function useKontaktAnlegen(kundeId: string) {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (felder: Partial<Kontakt>) =>
      api.senden<Kontakt>(`/api/kunden/${kundeId}/kontakte`, felder),
    onSuccess: () => speicher.invalidateQueries({ queryKey: aktenschluessel.kontakte(kundeId) }),
  })
}

export function useKontaktLoeschen(kundeId: string) {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.loeschen(`/api/kunden/${kundeId}/kontakte/${id}`),
    onSuccess: () => speicher.invalidateQueries({ queryKey: aktenschluessel.kontakte(kundeId) }),
  })
}

// ── Verträge ───────────────────────────────────────────────────────────

export function useVertraege(kundeId: string) {
  return useQuery({
    queryKey: aktenschluessel.vertraege(kundeId),
    queryFn: ({ signal }) => api.holen<Vertrag[]>(`/api/kunden/${kundeId}/vertraege`, signal),
    enabled: Boolean(kundeId),
  })
}

export function useVertragAnlegen(kundeId: string) {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (felder: Partial<Vertrag>) =>
      api.senden<Vertrag>(`/api/kunden/${kundeId}/vertraege`, felder),
    onSuccess: () =>
      speicher.invalidateQueries({ queryKey: aktenschluessel.vertraege(kundeId) }),
  })
}

export function useVertragAendern(kundeId: string) {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...felder }: { id: string } & Partial<Vertrag>) =>
      api.aendern<Vertrag>(`/api/kunden/${kundeId}/vertraege/${id}`, felder),
    onSuccess: () =>
      speicher.invalidateQueries({ queryKey: aktenschluessel.vertraege(kundeId) }),
  })
}

export function useVertragLoeschen(kundeId: string) {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.loeschen(`/api/kunden/${kundeId}/vertraege/${id}`),
    onSuccess: () =>
      speicher.invalidateQueries({ queryKey: aktenschluessel.vertraege(kundeId) }),
  })
}

// ── Ablage (Verwaltung) ────────────────────────────────────────────────

export type AblageStand = {
  art: Ablageart
  vollstaendig: boolean
  webdav_url: string | null
  webdav_benutzer: string | null
  webdav_passwort_gesetzt: boolean
  s3_endpunkt: string | null
  s3_region: string | null
  s3_eimer: string | null
  s3_schluessel_id: string | null
  s3_geheimnis_gesetzt: boolean
  sp_host: string | null
  sp_pfad: string | null
  sp_ordner: string
  dokumente_gesamt: number
  bytes_gesamt: number
}

export function useAblage() {
  return useQuery({
    queryKey: aktenschluessel.ablage,
    queryFn: ({ signal }) => api.holen<AblageStand>('/api/ablage', signal),
  })
}

export function useAblageSetzen() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (felder: Record<string, unknown>) =>
      api.setzen<AblageStand>('/api/ablage', felder),
    onSuccess: (stand) => speicher.setQueryData(aktenschluessel.ablage, stand),
  })
}

export function useAblageProbe() {
  return useMutation({
    mutationFn: () =>
      api.senden<{ erfolgreich: boolean; meldung: string }>('/api/ablage/probe'),
  })
}

/** Bytes lesbar machen: 1,4 MB statt 1468006. */
export function bytesLesbar(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const einheiten = ['KB', 'MB', 'GB', 'TB']
  let wert = bytes / 1024
  let i = 0
  while (wert >= 1024 && i < einheiten.length - 1) {
    wert /= 1024
    i += 1
  }
  return `${wert.toLocaleString('de-DE', { maximumFractionDigits: 1 })} ${einheiten[i]}`
}

// ── Beispielkunde ──────────────────────────────────────────────────────

export type BeispielStand = {
  vorhanden: boolean
  kunde_id: string | null
  name: string | null
}

export const beispielschluessel = ['beispielkunde'] as const

export function useBeispiel() {
  return useQuery({
    queryKey: beispielschluessel,
    queryFn: ({ signal }) => api.holen<BeispielStand>('/api/beispielkunde', signal),
  })
}

/**
 * Nach dem Anlegen oder Entfernen ist fast alles anders: Kundenliste,
 * Prüfungen, Fälligkeiten, Übersicht. Statt einzelne Schlüssel aufzuzählen
 * – und beim nächsten neuen Schlüssel einen zu vergessen – wird alles
 * verworfen. Das passiert genau zweimal je Einführung.
 */
function allesVerwerfenNach(speicher: ReturnType<typeof useQueryClient>) {
  void speicher.invalidateQueries()
}

export function useBeispielAnlegen() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: () => api.senden<BeispielStand>('/api/beispielkunde'),
    onSuccess: () => allesVerwerfenNach(speicher),
  })
}

export function useBeispielEntfernen() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: () => api.loeschen('/api/beispielkunde'),
    onSuccess: () => allesVerwerfenNach(speicher),
  })
}

// ── Nummernkreise ──────────────────────────────────────────────────────

export type Nummernkreisart =
  | 'kunde'
  | 'bericht'
  | 'angebot'
  | 'auftragsbestaetigung'
  | 'rechnung'

export const NUMMERNKREISTEXT: Record<Nummernkreisart, string> = {
  kunde: 'Kundennummer',
  bericht: 'Berichtsnummer',
  angebot: 'Angebotsnummer',
  auftragsbestaetigung: 'Auftragsnummer',
  rechnung: 'Rechnungsnummer',
}

/** Was der Kreis nummeriert – und was daran wichtig ist. */
export const NUMMERNKREISHINWEIS: Record<Nummernkreisart, string> = {
  kunde:
    'Wird bisher von Hand vergeben. Eingeschaltet bekommt jeder neue Kunde ' +
    'automatisch die nächste Nummer.',
  bericht: 'Steht auf jedem Prüfbericht. Wird beim Abschluss einer Prüfung vergeben.',
  angebot: 'Wird beim Herausgeben eines Angebots vergeben.',
  auftragsbestaetigung: 'Wird beim Herausgeben einer Auftragsbestätigung vergeben.',
  rechnung:
    'Gilt auch für Stornorechnungen: steuerlich sind sie Rechnungen, und die ' +
    'Folge muss lückenlos bleiben.',
}

export type Nummernkreis = {
  art: Nummernkreisart
  praefix: string
  mit_jahr: boolean
  stellen: number
  startnummer: number
  trenner: string
  automatisch: boolean
  beispiel: string
  vergeben: string | null
}

export const nummernkreisschluessel = ['nummernkreise'] as const

export function useNummernkreise() {
  return useQuery({
    queryKey: nummernkreisschluessel,
    queryFn: ({ signal }) => api.holen<Nummernkreis[]>('/api/nummernkreise', signal),
  })
}

export function useNummernkreisSetzen() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: ({ art, ...felder }: { art: Nummernkreisart } & Partial<Nummernkreis>) =>
      api.setzen<Nummernkreis>(`/api/nummernkreise/${art}`, felder),
    onSuccess: () => speicher.invalidateQueries({ queryKey: nummernkreisschluessel }),
  })
}

// ── Versandsperre ──────────────────────────────────────────────────────

export type Versandmodus = 'normal' | 'intern' | 'umleitung' | 'aus'

export const VERSANDMODUSTEXT: Record<Versandmodus, string> = {
  normal: 'Normal — alles geht hinaus',
  intern: 'Nur an eigene Mitarbeiter',
  umleitung: 'Alles an eine Adresse umleiten',
  aus: 'Aus — nichts geht hinaus',
}

export const VERSANDMODUSHINWEIS: Record<Versandmodus, string> = {
  normal: 'Der Regelfall. Bestätigungen, Erinnerungen und Zusagen gehen an die Empfänger.',
  intern:
    'Der Modus für eine Datenübernahme: Ihre Mitarbeiter bekommen weiter Post, ' +
    'Kunden nichts. Entschieden wird über den Benutzerbestand, nicht über die ' +
    'Domäne der Adresse.',
  umleitung:
    'Zum Ansehen, wie eine Mail wirklich aussieht. Alles geht an die eine ' +
    'Adresse; im Text steht, an wen sie eigentlich gegangen wäre.',
  aus: 'Nichts geht hinaus — auch nicht an Sie selbst. Alles steht im Postausgang.',
}

export type VersandStand = {
  modus: Versandmodus
  umleitung_an: string | null
  eingeschraenkt: boolean
  unterdrueckt_7_tage: number
}

export type Versandstatus =
  | 'verschickt'
  | 'unterdrueckt'
  | 'umgeleitet'
  | 'gescheitert'
  | 'ohne_weg'

export const VERSANDSTATUSTEXT: Record<Versandstatus, string> = {
  verschickt: 'verschickt',
  unterdrueckt: 'zurückgehalten',
  umgeleitet: 'umgeleitet',
  gescheitert: 'gescheitert',
  ohne_weg: 'kein Versandweg',
}

export type PostausgangZeile = {
  id: string
  zeitpunkt: string
  an: string
  tatsaechlich_an: string | null
  betreff: string
  modus: Versandmodus
  status: Versandstatus
  weg: string | null
  fehler: string | null
}

export const versandschluessel = ['mailversand'] as const

export function useVersand() {
  return useQuery({
    queryKey: versandschluessel,
    queryFn: ({ signal }) => api.holen<VersandStand>('/api/mailversand', signal),
    // Der Warnstreifen soll nicht ewig eine Sperre zeigen, die längst
    // aufgehoben ist – und nicht ewig keine, die längst gesetzt wurde.
    staleTime: 60_000,
  })
}

export function useVersandSetzen() {
  const speicher = useQueryClient()
  return useMutation({
    mutationFn: (felder: { modus?: Versandmodus; umleitung_an?: string }) =>
      api.setzen<VersandStand>('/api/mailversand', felder),
    onSuccess: (stand) => {
      speicher.setQueryData(versandschluessel, stand)
      void speicher.invalidateQueries({ queryKey: ['postausgang'] })
    },
  })
}

export function usePostausgang(nurUnterdrueckt = false) {
  return useQuery({
    queryKey: ['postausgang', nurUnterdrueckt],
    queryFn: ({ signal }) =>
      api.holen<PostausgangZeile[]>(
        `/api/postausgang?nur_unterdrueckt=${nurUnterdrueckt}`,
        signal,
      ),
  })
}

export function usePostausgangText(id: string | null) {
  return useQuery({
    queryKey: ['postausgang', 'text', id],
    queryFn: ({ signal }) =>
      api.holen<PostausgangZeile & { text: string }>(`/api/postausgang/${id}`, signal),
    enabled: Boolean(id),
  })
}
